// Phase 2 — Shared building blocks for the Login flow extracted from
// Login.jsx so each stage component (LoginForm / TwoFactorAuth /
// PasswordReset) can render consistent inputs without copy-pasting.
//
// All exports are pure presentational — no auth state, no API calls.

import { forwardRef } from "react";
import { ShieldCheck } from "lucide-react";

export const inputCls =
  "w-full px-3.5 h-11 border border-[var(--line-2)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/15 bg-[var(--surface)] outline-none rounded-sm text-[14px] transition-all";

export const primaryBtn =
  "w-full h-11 bg-[var(--accent)] hover:bg-[var(--accent)]/90 active:bg-[var(--accent)] text-white font-medium rounded-sm text-[13.5px] disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md";

export const secondaryBtn =
  "w-full h-11 bg-[var(--surface)] hover:bg-[var(--surface-2)] border border-[var(--line-2)] hover:border-[var(--ink-2)] text-[var(--ink)] font-medium rounded-sm text-[13.5px] disabled:opacity-50 inline-flex items-center gap-2 px-3.5 transition-all";

export function LogoTile({ logo_data_url, alt, small }) {
  // Slightly oversized logo per UX feedback; responsive scaling so phones
  // don't oversize the brand mark vs the form.
  const size = small
    ? "h-12 w-12 sm:h-14 sm:w-14"
    : "h-14 w-14 sm:h-16 sm:w-16 lg:h-[68px] lg:w-[68px]";
  const imgSize = small
    ? "h-9 w-9 sm:h-11 sm:w-11"
    : "h-11 w-11 sm:h-12 sm:w-12 lg:h-14 lg:w-14";
  const src = logo_data_url || "/bizassist-logo.png?v=2";
  return (
    <div className={`${size} rounded-sm bg-white border border-[var(--line-2)] shadow-sm grid place-items-center overflow-hidden shrink-0`}>
      <img src={src} alt={alt} className={`${imgSize} object-contain`} />
    </div>
  );
}

export function TrustChip({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-[var(--surface)]/80 backdrop-blur-sm border border-[var(--line)] text-[11.5px] text-[var(--ink)]">
      <Icon size={13} className="text-[var(--accent)] shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

export function StageHeader({ stage, role }) {
  const roleLabel = role === "partner" ? "Partner" : role === "merchant" ? "Merchant" : "";
  const copy = {
    password: { eye: "Welcome back", head: "Sign in to continue", sub: "Enter your credentials to access your commission dashboard." },
    "2fa_setup": { eye: "Set up 2FA", head: "Scan the QR code", sub: "One-time setup to secure your account." },
    "2fa_verify": { eye: "Two-factor auth", head: "Enter your 6-digit code", sub: "Grab the current code from your authenticator app." },
    forgot: { eye: "Password reset", head: "Forgot your password?", sub: "No worries — we'll email you a secure reset link." },
  }[stage];
  if (roleLabel && stage === "password") {
    return (
      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent)] font-medium">{roleLabel} portal</div>
        <h2 className="font-display font-medium text-[30px] sm:text-[34px] leading-[1.05] tracking-tight text-[var(--ink)]">
          Welcome, {roleLabel.toLowerCase()}
        </h2>
        <p className="text-[13.5px] text-[var(--muted)] leading-relaxed max-w-sm">
          {role === "partner"
            ? "Sign in to view your merchants, payouts, and statements."
            : "Sign in to view your volumes and commercials across payment gateways."}
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent)] font-medium">{copy.eye}</div>
      <h2 className="font-display font-medium text-[30px] sm:text-[34px] leading-[1.05] tracking-tight text-[var(--ink)]">{copy.head}</h2>
      <p className="text-[13.5px] text-[var(--muted)] leading-relaxed max-w-sm">{copy.sub}</p>
    </div>
  );
}

export function Field({ label, icon: Icon, children, trailing, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] uppercase tracking-[0.15em] text-[var(--muted)] font-semibold inline-flex items-center gap-1.5">
          {Icon && <Icon size={11} className="text-[var(--muted)]" />} {label}
        </label>
        {trailing}
      </div>
      {children}
    </div>
  );
}

export function Banner({ tone, icon: Icon, children }) {
  const cls =
    tone === "amber"
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-[var(--surface-2)] border-[var(--line)] text-[var(--ink)]";
  const IconCmp = Icon || ShieldCheck;
  return (
    <div className={`flex items-start gap-2.5 border rounded-sm p-3 text-[12.5px] leading-relaxed ${cls}`}>
      <IconCmp size={15} className={`shrink-0 mt-0.5 ${tone === "amber" ? "" : "text-[var(--accent)]"}`} />
      <div>{children}</div>
    </div>
  );
}

export const CodeInput = forwardRef(function CodeInput({ value, onChange }, ref) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[var(--muted)] font-semibold block mb-1.5">
        6-digit code
      </label>
      <input
        ref={ref}
        data-testid="twofa-code"
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="w-full px-3 h-14 border border-[var(--line-2)] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 bg-[var(--surface)] outline-none rounded-sm font-mono text-[28px] text-center tracking-[0.45em] transition-all"
        placeholder="000000"
        autoComplete="one-time-code"
      />
    </div>
  );
});
